/* eslint-disable no-unused-vars */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withNavigation } from 'src/ui/hoc';
import { Button, Column, MuiTitle, Container, Field } from 'src/ui/mui-ui-kit';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Form, Field as FormField, FormSpy } from 'react-final-form';
import propTypes from 'prop-types';

import { UiInputAdapter, UiToastAdapter } from 'src/ui';
import { composeValidators, required, email } from 'src/ui/validations';
import { toast } from 'react-toastify';
import { signIn, clearAuthErrors, getToken } from '../index.js';
// Import action types directly from the actions file
import { SIGN_IN_SUCCESS, SIGN_IN_FAILURE } from '../redux/actions';
import { ResetPasswordModal, AuthCardLayout } from '../components/index.js';
import { getHostBrand } from '../hostBranding';

class Login extends Component {
  state = {
    resettingPassword: false,
    currentEmail: '',
    isSubmitting: false, // Add this new state variable
  };

  brand = getHostBrand();

  componentDidMount() {
    const { clearAuthErrors } = this.props;
    clearAuthErrors();
  }

  componentDidUpdate(prevProps) {
    const { token } = this.props;

    if (prevProps.token.isLoading() && !token.isLoading()) {
      // console.log('Token finished loading, checking for 2FA requirement...');

      // 2FA-required responses are handled (and navigated) by handleSubmit's
      // action callback — App treats a 2FA-pending token as unauthenticated,
      // so plain router navigation works and nothing must happen here. The
      // errorCode check catches backends that deliver 2fa_required as an
      // HTTP error: the reducer's ERROR case leaves token.data untouched,
      // so only the error traits carry the marker for that shape.
      if (
        (token.data &&
          (token.data.code === '2fa_required' || token.data.is2faRequired)) ||
        (token.errorCode && token.errorCode() === '2fa_required')
      ) {
        return;
      }

      // Normal login success needs nothing here either: the token becomes
      // fully authenticated and App switches route trees by itself. (A
      // former token.data.access check was dead code — the reducer stores
      // accessToken, so that field never existed.)
      const errorMessage = token.errorMessage ? token.errorMessage() : null;
      if (errorMessage) {
        toast(UiToastAdapter(errorMessage, 'danger'));
      }
    }
  }

  handleSubmit = values => {
    const { signIn, history } = this.props;
    const { email, password } = values;

    // Set submitting state to true to disable the button
    this.setState({ currentEmail: email, isSubmitting: true });

    // console.log('Submitting login for:', email);

    // Dispatch the sign in action
    signIn({
      email,
      password,
    })
      .then(action => {
        // console.log('Sign in action response:', action);

        // Safely check the response type and payload
        if (action && action.type) {
          // Handle success case
          if (action.type === SIGN_IN_SUCCESS) {
            // console.log('Sign in success action:', action.payload);

            // Check if this is a 2FA response
            if (
              action.payload &&
              (action.payload.code === '2fa_required' ||
                action.payload.is2faRequired)
            ) {
              const emailToUse = action.payload.email || email;
              history.push(
                `/two-factor-auth?email=${encodeURIComponent(emailToUse)}`
              );
            }
          }
          // Handle failure case
          else if (action.type === SIGN_IN_FAILURE) {
            // console.log('Sign in failure action:', action.payload);

            // Check if this is a 2FA response masquerading as an error
            if (action.payload && action.payload.code === '2fa_required') {
              history.push(
                `/two-factor-auth?email=${encodeURIComponent(email)}`
              );
            } else {
              // If not a 2FA response, show error and re-enable the button
              const errorMessage =
                action.payload?.detail ||
                action.payload?.message ||
                'Invalid credentials. Please try again.';
              toast(UiToastAdapter(errorMessage, 'danger'));
              this.setState({ isSubmitting: false });
            }
          }
        } else {
          console.error('Invalid action response:', action);
          toast(
            UiToastAdapter('An unexpected error occurred. Please try again.', 'danger')
          );
          // Re-enable the button on error
          this.setState({ isSubmitting: false });
        }
      })
      .catch(error => {
        console.error('Error in sign in action:', error);
        toast(
          UiToastAdapter('An unexpected error occurred. Please try again.', 'danger')
        );
        // Re-enable the button on error
        this.setState({ isSubmitting: false });
      });
  };

  onToggleResetPassword = resettingPassword => {
    this.setState({ resettingPassword });
  };

  renderLoginForm() {
    const { isSubmitting } = this.state;

    return (
      <Form
        onSubmit={this.handleSubmit}
        render={({ handleSubmit, submitting, hasValidationErrors }) => (
          <>
            <FormSpy
              subscription={{
                values: {
                  email: true,
                },
              }}
              onChange={({ values: { email } }) => {
                this.setState({ currentEmail: email });
              }}
            />
            <form name="form" onSubmit={handleSubmit} className="login-form">
              <FormField
                name="email"
                component={UiInputAdapter}
                validate={composeValidators('Email', required, email)}
                label="Email"
                type="email"
                id="email"
                hasIcons="left"
                className="login-input"
                renderIcons={() => [
                  <span className="icon is-left is-small" key="envelope">
                    <FontAwesomeIcon icon={['fal', 'envelope']} size="sm" />
                  </span>,
                ]}
                disabled={submitting}
                autoFocus
                required
                hideAsterisk
              />

              <FormField
                name="password"
                component={UiInputAdapter}
                validate={required('Password')}
                label="Password"
                type="password"
                id="password"
                className="login-input"
                hasIcons="right"
                renderIcons={() => [
                  <span className="icon is-right is-small" key="lock">
                    <FontAwesomeIcon icon={['fal', 'lock-alt']} size="sm" />
                  </span>,
                ]}
                disabled={submitting}
                required
                hideAsterisk
              />

              <Field>
                <Button
                  isColor="gradient-bright"
                  className="is-long"
                  disabled={submitting || hasValidationErrors || isSubmitting}
                  isLoading={submitting || isSubmitting}
                  type="submit"
                >
                  Login
                </Button>
              </Field>
            </form>
          </>
        )}
      />
    );
  }

  render() {
    const { resettingPassword, currentEmail } = this.state;

    return (
      <>
        {this.brand === 'other' ? (
          <Container className="login">
            <Column>
              <MuiTitle isSize={4} tag="h2" hasTextColor="secondary">
                Login
              </MuiTitle>

              <p className="has-text-grey-medium-dark is-size-7">
                Please login to access your account.
              </p>

              {this.renderLoginForm()}

              <Button
                className="login-help-link is-paddingless"
                isColor="text"
                onClick={() => this.onToggleResetPassword(true)}
              >
                Need help logging in?
              </Button>
            </Column>
          </Container>
        ) : (
          <AuthCardLayout>
            <h2 className="is-sr-only">Login</h2>
            <p className="auth-card-subtitle">Log in to your account</p>

            {this.renderLoginForm()}

            <div className="auth-card-footer">
              <Button
                className="login-help-link is-paddingless"
                isColor="text"
                onClick={() => this.onToggleResetPassword(true)}
              >
                Need help logging in?
              </Button>
            </div>
          </AuthCardLayout>
        )}

        <ResetPasswordModal
          isActive={resettingPassword}
          currentEmail={currentEmail}
          onCancel={() => this.onToggleResetPassword(false)}
        />
      </>
    );
  }
}

Login.propTypes = {
  history: propTypes.object.isRequired,
  token: propTypes.object.isRequired,
  signIn: propTypes.func.isRequired,
  clearAuthErrors: propTypes.func.isRequired,
};

const mapStateToProps = state => ({
  token: getToken(state),
});

export const LoginPage = connect(mapStateToProps, { signIn, clearAuthErrors })(
  withNavigation(Login)
);
